import React from 'react'
import {createTheme} from '@material-ui/core/styles'
import { ChevronDown, X } from 'lucide-react'
import colors from '../assets/colors'
import { tokens, primitives } from '../assets/tokens'
import { safeDarken } from '../utils/color'
import _ from 'lodash'

const { color, shadow } = tokens
const RADIUS = 10 // radio de botones, cards y tablas en el ATS (hrbf_freezer GlobalTheme)
const RADIUS_INPUT = 8
const RADIUS_BUTTON = 8 // ActionButton del Storybook

// Elevaciones de MUI mapeadas a las sombras del design system (antes todas a 'none')
const shadows = ['none', shadow.sm, shadow.sm, ...Array(22).fill(shadow.md)]

// Chevron de lucide en selects y autocompletes, como AutocompleteSelect de Velora. Select le pasa
// la clase MuiSelect-icon (posición y giro al abrir).
const SelectChevron = props => React.createElement(ChevronDown, { size: 18, ...props })

export const clientMaterialTheme = client => {
  const clientBackground = _.get(client, 'background_color', colors.blue)
  const clientColorMain = _.get(client, 'text_color_principal', colors.white)
  const clientColorSecondary = _.get(client, 'text_color_secondary', colors.whiteAlpha50)
  // Gris neutro para todos los clientes: un tinte del color de marca quedaba raro con
  // colores de cliente intensos (amarillo, naranja...)
  const tableHeaderBackground = color.background.light

  return createTheme({
    colors,

    primary: clientBackground,
    textPrimary: clientColorMain,
    textSecondary: clientColorSecondary,

    fonts: {
      primary: tokens.typography.fontFamily.primary,
      secondary: tokens.typography.fontFamily.secondary
    },

    shadows,

    constants: {
      MENU_WIDTH: 80,
      DRAWER_WIDTH: 300
    },

    typography: {
      useNextVariants: true,
      fontFamily: tokens.typography.fontFamily.primary,
      fontSize: 18
    },

    palette: {
      primary: {
        main: clientBackground,
        contrastText: clientColorMain
      },
      secondary: {
        main: clientBackground,
        contrastText: colors.white
      },
      text: {
        primary: color.text.primary,
        secondary: color.text.secondary,
        disabled: color.text.disabled
      },
      background: {
        default: color.background.surface,
        paper: color.background.default
      },
      divider: color.border.subtle,
      error: { main: color.error.main },
      warning: { main: color.warning.main },
      success: { main: color.success.main },
      info: { main: color.info.main }
    },

    shape: {
      borderRadius: RADIUS
    },

    overrides: {
      // Réplica de ActionButton del Storybook de Velora. contained = filled en el color
      // del cliente; outlined = neutro. Afecta a todos los <Button> de MUI (cabeceras de
      // bandeja, modales...), que no pasan por RegularButton.
      MuiButton: {
        root: {
          height: 40,
          padding: '10px 14px',
          fontFamily: tokens.typography.fontFamily.primary,
          fontWeight: 400,
          fontSize: 14,
          lineHeight: 1.4,
          textTransform: 'unset',
          whiteSpace: 'nowrap',
          borderRadius: RADIUS_BUTTON
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          '&:active': { boxShadow: 'none' },
          '&$disabled': {
            color: primitives.neutral[400],
            backgroundColor: color.background.muted
          }
        },
        containedPrimary: {
          color: clientColorMain,
          backgroundColor: clientBackground,
          '&:hover': { backgroundColor: safeDarken(clientBackground) }
        },
        containedSecondary: {
          color: clientColorMain,
          backgroundColor: clientBackground,
          '&:hover': { backgroundColor: safeDarken(clientBackground) }
        },
        outlined: {
          padding: '10px 14px',
          border: `1.5px solid ${primitives.neutral[300]}`,
          color: primitives.neutral[600],
          backgroundColor: color.background.default,
          '&:hover': {
            border: `1.5px solid ${primitives.neutral[400]}`,
            backgroundColor: color.background.muted
          },
          '&$disabled': {
            border: `1.5px solid ${primitives.neutral[200]}`,
            color: primitives.neutral[400],
            backgroundColor: color.background.muted
          }
        }
      },
      // Inputs y selectores como CustomTextField / AutocompleteSelect del Storybook de Velora:
      // 40 px, borde de 1,5 px en gris, radio 8 y fondo blanco. Hover y foco en el color del cliente.
      MuiInputBase: {
        root: {
          fontFamily: tokens.typography.fontFamily.primary,
          fontSize: 14,
          color: color.text.primary
        },
        input: {
          '&::placeholder': {
            color: color.text.disabled,
            opacity: 1
          }
        }
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: RADIUS_INPUT,
          backgroundColor: color.background.default,
          '&:hover $notchedOutline': {
            borderColor: clientBackground
          },
          '&$focused $notchedOutline': {
            borderColor: clientBackground,
            borderWidth: 1.5
          },
          '&$error $notchedOutline': {
            borderColor: color.error.main
          },
          '&$disabled': {
            backgroundColor: color.background.muted
          },
          '&$disabled $notchedOutline': {
            borderColor: color.border.disabled
          }
        },
        notchedOutline: {
          borderWidth: 1.5,
          borderColor: color.border.default
        },
        input: {
          height: 40,
          padding: '0 14px',
          boxSizing: 'border-box'
        },
        inputMarginDense: {
          paddingTop: 0,
          paddingBottom: 0
        },
        multiline: {
          padding: '10px 14px'
        },
        inputMultiline: {
          height: 'auto',
          padding: 0
        },
        adornedStart: {
          paddingLeft: 12
        },
        inputAdornedStart: {
          paddingLeft: 0
        }
      },
      MuiInputLabel: {
        outlined: {
          fontSize: 14,
          transform: 'translate(14px, 12px) scale(1)',
          '&$shrink': {
            transform: 'translate(14px, -6px) scale(0.75)'
          }
        }
      },
      MuiSelect: {
        select: {
          '&:focus': {
            backgroundColor: 'transparent'
          }
        },
        selectMenu: {
          height: 40,
          minHeight: 0,
          lineHeight: '40px',
          boxSizing: 'border-box'
        },
        outlined: {
          '&&': {
            paddingRight: 36
          }
        },
        icon: {
          color: primitives.neutral[800],
          right: 12,
          top: 'calc(50% - 9px)'
        },
        iconOutlined: {
          right: 12
        }
      },
      MuiMenuItem: {
        root: {
          fontSize: 14,
          fontFamily: tokens.typography.fontFamily.primary
        }
      },
      MuiAutocomplete: {
        inputRoot: {
          '&[class*="MuiOutlinedInput-root"]': {
            paddingTop: 3,
            paddingBottom: 3,
            paddingLeft: 8,
            minHeight: 40,
            boxSizing: 'border-box'
          },
          '&[class*="MuiOutlinedInput-root"] $input': {
            height: 34,
            padding: '0 4px'
          },
          '&[class*="MuiOutlinedInput-root"] $input:first-child': {
            paddingLeft: 6
          },
          '&[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"]': {
            paddingTop: 3,
            paddingBottom: 3
          },
          '&[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"] $input': {
            padding: '0 4px'
          }
        },
        tag: {
          margin: 2,
          height: 26,
          fontSize: 12,
          borderRadius: 6,
          backgroundColor: color.background.light
        },
        // X y chevron del mismo tamaño y centrados en vertical sobre el input de 40 px
        endAdornment: {
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        },
        clearIndicator: {
          padding: 3,
          marginRight: 0,
          color: color.text.tertiary
        },
        popupIndicator: {
          padding: 3,
          marginRight: 0,
          color: primitives.neutral[800]
        },
        option: {
          fontSize: 14
        },
        noOptions: {
          fontSize: 14
        },
        loading: {
          fontSize: 14
        }
      },
      MuiPaper: {
        rounded: {
          borderRadius: RADIUS
        }
      },
      MuiTypography: {
        h1: {
          color: colors.black,
          fontWeight: 600,
          fontSize: 28,
          display: 'inline'
        },
        caption: {
          fontSize: 'inherit',
          fontFamily: 'inherit',
          lineHeight: 'inherit',
          fontWeight: 'inherit',
        }
      },
      MuiTable: {
        root: {}
      },
      MuiTableRow: {
        root: {
          // Sin cursor de mano aquí: material-table ya lo pone en las filas con onRowClick, y en las
          // tablas que no abren nada (usuarios, jobsites, etiquetas...) daba a entender que sí
          '&:hover': {
            backgroundColor: `${color.background.surface} !important`,
          },
        },
        footer: {
          '&:hover': {
            backgroundColor: 'transparent',
            boxShadow: 'none'
          }
        },
        head: {
          '&:hover': {
            boxShadow: 'none !important',
            cursor: 'inherit',
            backgroundColor: `${tableHeaderBackground} !important`,
          },
          '& th:hover': {
            backgroundColor: `${tableHeaderBackground} !important`,
          },
        }
      },
      MuiTableBody: {
        root: {}
      },
      MuiDialogContent: {
        root: {
          fontSize: 18,
        }
      },
      // Cabecera y filas de DataTable del Storybook de Velora (cabecera 56 px, filas 64 px)
      MuiTableHead: {
        root: {
          textAlign: 'left',
        }
      },
      MuiTableCell: {
        root: {
          borderBottom: `1px solid ${primitives.neutral[200]}`,
          // Columna de selección de material-table: llega con padding none y un ancho inline del
          // tamaño del icono, y el checkbox queda pegado al borde. Ancho de la DataTable de Velora.
          '&.MuiTableCell-paddingNone:first-child:has(.MuiCheckbox-root)': {
            width: '56px !important',
            minWidth: 56,
            padding: '0 8px 0 12px',
            boxSizing: 'border-box',
          },
        },
        head: {
          height: 56,
          padding: '0 16px',
          color: primitives.neutral[800],
          backgroundColor: `${tableHeaderBackground} !important`,
          borderBottom: 'none',
          textAlign: 'left !important',
          fontFamily: tokens.typography.fontFamily.primary,
          fontSize: 14,
          fontWeight: 400,
          lineHeight: 1.3,
          '&.MuiTableCell-alignRight': { textAlign: 'right !important' },
          '&:first-child': { borderTopLeftRadius: 16 },
          '&:last-child': { borderTopRightRadius: 16 },
        },
        body: {
          height: 64,
          padding: '8px 16px',
          textAlign: 'left',
          '&.MuiTableCell-alignRight': { textAlign: 'right' },
          fontSize: 14,
          color: primitives.neutral[800],
        },
        footer: {
          fontSize: '12px !important',
          color: `${color.text.tertiary} !important`,
          backgroundColor: `${color.background.light} !important`,
          borderBottom: `1px solid ${color.border.subtle}`,
          fontWeight: 'bold'
        }
      },
      MuiTableSortLabel: {
        root: {
          color: primitives.neutral[800],
          fontWeight: 400,
          fontSize: 14,

          // Sin negrita en la columna ordenada: todas las cabeceras al mismo peso; la flecha ya la marca
          '&$active': {
            color: color.text.primary,
          },
          '&:hover': {
            color: color.text.primary,
          },
          '&:focus': {
            color: color.text.primary,
          }
        },
        icon: {
          color: `${clientBackground} !important`,
        }
      },
      MuiTablePagination:{
        input: {
          background: color.background.light,
          border: `1px solid ${color.border.default}`,
          borderRadius: RADIUS_INPUT
        }
      },
      MuiLinearProgress: {
        root: {
          '&.MuiLinearProgress-indeterminate': {
            width: '100%',
            position: 'fixed',
            left: '80px',
            top: '0px',
          }
        }
      },
      MuiTooltip: {
        tooltip: {
          opacity: 0.95,
          fontSize: 12,
          fontFamily: tokens.typography.fontFamily.primary,
          fontWeight: 400,
          color: colors.black,
          backgroundColor: color.background.default,
          border: "1px solid " + clientBackground ,
          borderRadius: RADIUS_INPUT,
          boxShadow: shadow.sm,
          padding: "10px",
        }
      }
    },

    props: {
      MuiButton: {
        disableElevation: true
      },
      MuiSelect: {
        IconComponent: SelectChevron
      },
      MuiAutocomplete: {
        popupIcon: React.createElement(ChevronDown, { size: 18 }),
        closeIcon: React.createElement(X, { size: 16 })
      }
    }
  })
}

export default clientMaterialTheme()
